// "Load more" for product grids (snippets/ec-load-more.liquid).
// "Load more" fetches the next page through the Section Rendering API, adds its cards to the grid
// and moves the URL to that page (replaceState, so Back still leaves the list in one step).
// "Show previous" adds the earlier page above without moving what is on screen.
// Before leaving the page the loaded cards and the scroll position are kept for this tab
// (sessionStorage), so Back from a product shows the same list at the same place.

const STORAGE_KEY = 'ec-load-more'

function cleanUrl(href) {
  const url = new URL(href, window.location.href)
  url.searchParams.delete('section_id')
  return url
}

function parts(root = document) {
  return {
    grid: root.querySelector('[data-ec-grid]'),
    top: root.querySelector('[data-ec-load-more="top"]'),
    bottom: root.querySelector('[data-ec-load-more="bottom"]'),
    articles: root.querySelector('.ec-search-other__list')
  }
}

function cardsIn(grid) {
  return grid ? [...grid.children].filter((el) => el.classList.contains('grid-item')) : []
}

async function fetchPage(href, sectionId) {
  const url = cleanUrl(href)
  url.searchParams.set('section_id', sectionId)
  const response = await fetch(url)
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return new DOMParser().parseFromString(await response.text(), 'text/html')
}

// Keep the list for Back from a product page (only once more than one page is on screen)
function save() {
  const { grid, top, bottom, articles } = parts()
  if (!grid || !grid.hasAttribute('data-ec-loaded')) return
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        url: window.location.href,
        grid: grid.innerHTML,
        top: top ? top.outerHTML : '',
        bottom: bottom ? bottom.outerHTML : '',
        articles: articles ? articles.innerHTML : null,
        scroll: window.scrollY
      })
    )
  } catch (e) {
    // Storage full or blocked: Back then shows the current page only
  }
}

function restore() {
  const navigation = performance.getEntriesByType?.('navigation')[0]
  if (navigation?.type !== 'back_forward') return
  let saved
  try {
    saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY))
  } catch (e) {
    return
  }
  if (!saved || saved.url !== window.location.href) return

  const { grid, top, bottom, articles } = parts()
  if (!grid) return
  grid.innerHTML = saved.grid
  grid.setAttribute('data-ec-loaded', '')
  if (top && saved.top) top.outerHTML = saved.top
  else if (top) top.remove()
  else if (saved.top) grid.insertAdjacentHTML('beforebegin', saved.top)
  if (bottom && saved.bottom) bottom.outerHTML = saved.bottom
  if (articles && saved.articles !== null) articles.innerHTML = saved.articles

  if ('scrollRestoration' in history) history.scrollRestoration = 'manual'
  const scrollBack = () => window.scrollTo(0, saved.scroll)
  scrollBack()
  window.addEventListener('load', scrollBack, { once: true })
}

class EcLoadMore extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController()
    this.addEventListener('click', this.onClick.bind(this), { signal: this.abortController.signal })
  }

  disconnectedCallback() {
    this.abortController.abort()
  }

  onClick(evt) {
    const link = evt.target.closest('a[data-ec-load-page]')
    if (!link || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.button > 0) return
    evt.preventDefault()
    if (link.getAttribute('aria-disabled') === 'true') return
    if (link.dataset.ecLoadPage === 'next') {
      this.loadNext(link)
    } else {
      this.loadPrevious(link)
    }
  }

  async load(link, apply) {
    const { grid } = parts()
    link.setAttribute('aria-disabled', 'true')
    grid?.setAttribute('aria-busy', 'true')
    try {
      const doc = await fetchPage(link.href, this.dataset.sectionId)
      apply(doc, parts(doc))
      grid?.setAttribute('data-ec-loaded', '')
    } catch (e) {
      // Fall back to an ordinary page load
      window.location.href = cleanUrl(link.href).toString()
    } finally {
      link.removeAttribute('aria-disabled')
      grid?.removeAttribute('aria-busy')
    }
  }

  loadNext(link) {
    const href = cleanUrl(link.href)
    this.load(link, (doc, next) => {
      const { grid, bottom, articles } = parts()
      const newCards = cardsIn(next.grid)
      newCards.forEach((card) => grid.appendChild(document.importNode(card, true)))

      // Search: articles and pages from later pages join the existing group
      if (articles && next.articles) {
        next.articles.querySelectorAll('.ec-search-other__item').forEach((item) => {
          articles.appendChild(document.importNode(item, true))
        })
      }

      // Same element stays (its role="status" text is announced); text and link are updated
      const status = bottom.querySelector('.ec-load-more__status')
      const nextStatus = next.bottom?.querySelector('.ec-load-more__status')
      if (status && nextStatus) status.textContent = nextStatus.textContent
      const nextLink = next.bottom?.querySelector('[data-ec-load-page="next"]')
      if (nextLink) {
        link.href = cleanUrl(nextLink.getAttribute('href')).toString()
      } else {
        link.remove()
      }

      history.replaceState(history.state, '', href)

      // Keyboard and screen reader users continue at the first new product
      const firstLink = grid.querySelector(`.grid-item:nth-child(${grid.children.length - newCards.length + 1}) a`)
      firstLink?.focus({ preventScroll: true })
    })
  }

  loadPrevious(link) {
    this.load(link, (doc, previous) => {
      const { grid, top } = parts()
      const anchor = cardsIn(grid)[0]
      const before = anchor ? anchor.getBoundingClientRect().top : 0

      const fragment = document.createDocumentFragment()
      cardsIn(previous.grid).forEach((card) => fragment.appendChild(document.importNode(card, true)))
      grid.insertBefore(fragment, grid.firstChild)

      const previousLink = previous.top?.querySelector('[data-ec-load-page="previous"]')
      if (previousLink) {
        link.href = cleanUrl(previousLink.getAttribute('href')).toString()
      } else {
        top.remove()
      }

      // Keep the cards the person was looking at in the same place
      if (anchor) window.scrollBy(0, anchor.getBoundingClientRect().top - before)
    })
  }
}

if (!customElements.get('ec-load-more')) {
  restore()
  window.addEventListener('pagehide', save)
  customElements.define('ec-load-more', EcLoadMore)
}
